import { useState, useRef, useEffect, useCallback } from 'react';
import type { DeviceType } from './types';
import { DEVICE_CONFIGS } from './deviceConfigs';

export function usePreviewState(isOpen: boolean, onClose: () => void, refreshTrigger: number) {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('mobile');
  const [isRotated, setIsRotated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const deviceConfig = DEVICE_CONFIGS[selectedDevice];
  const frameWidth = isRotated ? deviceConfig.height : deviceConfig.width;
  const frameHeight = isRotated ? deviceConfig.width : deviceConfig.height;

  // Add padding for device frame (phone/tablet bezel)
  const totalFrameWidth = selectedDevice === 'desktop' ? frameWidth + 16 : frameWidth + 32;
  const totalFrameHeight = selectedDevice === 'desktop' ? frameHeight + 40 : frameHeight + 32;

  // Calculate scale to fit device in available space
  useEffect(() => {
    if (!isOpen) return;

    const calculateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const availableWidth = container.clientWidth - 48;
      const availableHeight = container.clientHeight - 48;

      const scaleX = availableWidth / totalFrameWidth;
      const scaleY = availableHeight / totalFrameHeight;
      const newScale = Math.min(scaleX, scaleY, 1);

      setScale(newScale);
    };

    const rafId = requestAnimationFrame(calculateScale);
    window.addEventListener('resize', calculateScale);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', calculateScale);
    };
  }, [isOpen, totalFrameWidth, totalFrameHeight, selectedDevice, isRotated]);

  // Refresh iframe when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && iframeRef.current) {
      setIsLoading(true);
      const url = new URL(iframeRef.current.src, window.location.origin);
      url.searchParams.set('_t', Date.now().toString());
      iframeRef.current.src = url.toString();
    }
  }, [refreshTrigger]);

  // Handle escape key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  const handleRefresh = useCallback(() => {
    if (iframeRef.current) {
      setIsLoading(true);
      const url = new URL(iframeRef.current.src, window.location.origin);
      url.searchParams.set('_t', Date.now().toString());
      iframeRef.current.src = url.toString();
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleDeviceChange = useCallback((device: DeviceType) => {
    setSelectedDevice(device);
    setIsRotated(false);
  }, []);

  return {
    // Refs
    iframeRef,
    containerRef,
    // Device state
    selectedDevice,
    isRotated,
    setIsRotated,
    handleDeviceChange,
    // Dimensions
    frameWidth,
    frameHeight,
    totalFrameWidth,
    totalFrameHeight,
    scale,
    // Loading state
    isLoading,
    handleRefresh,
    handleIframeLoad,
  };
}
